import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AllLocationLinks } from '../components/LocationLinks';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Services = () => {
  const allServices = [
    {
      name: 'FBO & Private Airport Transportation',
      description: 'Professional chauffeur service for private jet travelers at all major FBOs including Signature Flight Support, Atlantic Aviation, and Sheltair.',
      features: [
        'Wait-and-greet service',
        'FBO familiarization',
        'Luggage handling',
        'Flight tracking',
        'Discreet pickup and drop-off',
        'Private jet coordination',
      ],
      link: '/fbo-private-airport-chauffeur',
    },
    {
      name: 'Hourly As-Directed Service',
      description: 'Flexible hourly bookings for meetings, events, and as-directed transportation throughout our service areas.',
      features: [
        'Corporate meetings',
        'Special events',
        'City tours',
        'Multi-stop service',
        'On-demand flexibility',
        'Professional chauffeurs',
      ],
      link: '/hourly-as-directed-chauffeur',
    },
    {
      name: 'Executive Car Service',
      description: 'Premium transportation for corporate clients and business travelers with a focus on comfort and efficiency.',
      features: [
        'Airport transfers',
        'Corporate accounts',
        'Business meetings',
        'VIP transportation',
        'Confidentiality',
        'Luxury sedans and SUVs',
      ],
      link: '/executive-car-service-newark-nj',
    },
    {
      name: 'Airport Transfer',
      description: 'Reliable airport transportation to and from all major airports in our service states.',
      features: [
        'Meet & greet',
        'Flight tracking',
        'Luggage assistance',
        '24/7 availability',
        'All major airports',
      ],
      link: '/services',
    },
    {
      name: 'Corporate Transportation',
      description: 'Comprehensive corporate travel solutions for executives, teams, and VIPs.',
      features: [
        'Corporate accounts',
        'Event transportation',
        'Roadshows',
        'Group transfers',
        'Business-class vehicles',
      ],
      link: '/services',
    },
    {
      name: 'Special Event Chauffeur',
      description: 'Luxury transportation for weddings, galas, concerts, and special occasions.',
      features: [
        'Wedding transportation',
        'Red carpet events',
        'Concerts & sports',
        'Proms & graduations',
        'Custom itineraries',
      ],
      link: '/services',
    },
    {
      name: 'Long-Distance & Interstate Rides',
      description: 'Comfortable and safe long-distance travel between states and major cities.',
      features: [
        'Interstate travel',
        'Door-to-door service',
        'Luxury vehicles',
        'Professional drivers',
        'Flexible scheduling',
      ],
      link: '/services',
    },
  ];

  const serviceStates = [
    'New York',
    'Massachusetts',
    'Pennsylvania',
    'Maine',
    'New Jersey',
    'District of Columbia',
    'Connecticut',
    'Rhode Island',
    'Vermont',
    'New Hampshire',
    'Maryland',
    'Delaware',
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'TiLimousine Services',
    provider: {
      '@type': 'Organization',
      name: 'TiLimousine',
      url: 'https://tilimousine.com'
    },
    serviceType: allServices.map(s => s.name),
    areaServed: serviceStates.map(state => ({ '@type': 'State', name: state })),
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'TiLimousine Services',
      itemListElement: allServices.map(service => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: service.name,
          description: service.description
        }
      }))
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Our Services | TiLimousine</title>
        <meta name="description" content="Premium chauffeur services (FBO, hourly, executive, airport, corporate, special events, and interstate rides) available 24/7 across New York, Massachusetts, Pennsylvania, Maine, New Jersey, DC, Connecticut, Rhode Island, Vermont, New Hampshire, Maryland, and Delaware. Professional drivers and luxury vehicles." />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Navbar />

      <div className="container mx-auto px-4 py-16">
        {/* Breadcrumb Navigation */}
        <nav className="mb-8 flex items-center space-x-2 text-sm">
          <Link to="/" className="text-gold hover:text-gold/80">Home</Link>
          <span className="text-gold">/</span>
          <span className="text-white">Services</span>
        </nav>

        <h1 className="text-4xl md:text-5xl font-cinzel text-gold mb-8">Our Services</h1>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {allServices.map((service, idx) => (
            <div key={idx} className="bg-gray-900 p-8 rounded-lg hover:bg-gray-800 transition-colors">
              <h2 className="text-2xl font-semibold text-gold mb-4">{service.name}</h2>
              <p className="mb-4">{service.description}</p>
              <ul className="list-disc list-inside space-y-2 text-white/80 mb-2">
                {service.features.map((feature, fidx) => (
                  <li key={fidx}>{feature}</li>
                ))}
              </ul>
              {service.link && (
                <Link to={service.link} className="text-gold underline hover:text-gold/80">Learn More</Link>
              )}
              <div className="mt-4">
                {['FBO & Private Airport Transportation', 'Airport Transfer'].includes(service.name) ? (
                  <button
                    className="inline-block bg-gold text-black px-4 py-2 rounded-full hover:bg-gold/80 transition-colors font-semibold"
                    onClick={() => {
                      const subject = `TiLimousine Booking Request for ${service.name}`;
                      const body = `Service: ${service.name}\nName: \nPhone: \nPickup Location: \nDrop-off Location: \nDate & Time: \nFlight Time: \nFlight Number: \nSpecial Requests: `;
                      window.location.href = `mailto:contact@tilimousine.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                    }}
                  >
                    Book Now
                  </button>
                ) : (
                  <button
                    className="inline-block bg-gold text-black px-4 py-2 rounded-full hover:bg-gold/80 transition-colors font-semibold"
                    onClick={() => {
                      const subject = `TiLimousine Booking Request for ${service.name}`;
                      const body = `Service: ${service.name}\nName: \nPhone: \nPickup Location: \nDrop-off Location: \nDate & Time: \nSpecial Requests: `;
                      window.location.href = `mailto:contact@tilimousine.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                    }}
                  >
                    Book Now
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Service Areas */}
        <section className="mb-16">
          <h2 className="text-3xl font-cinzel text-gold mb-8">Our Service States</h2>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {serviceStates.map((state, idx) => (
              <li key={idx} className="flex items-center">
                <span className="text-gold mr-2">•</span>
                <span>{state}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Trust Signals */}
        <section className="mb-16">
          <h2 className="text-3xl font-cinzel text-gold mb-8">Why Choose TiLimousine?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gold mb-4">Professional Drivers</h3>
              <p>Background-checked chauffeurs with extensive local knowledge and professional training.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gold mb-4">Luxury Fleet</h3>
              <p>Modern, well-maintained vehicles equipped with amenities for a premium experience.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gold mb-4">24/7 Service</h3>
              <p>Round-the-clock availability with responsive dispatch and customer support.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gold mb-4">FBO Expertise</h3>
              <p>Specialized service for private jet terminals with comprehensive FBO knowledge.</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <h2 className="text-3xl font-cinzel text-gold mb-6">Ready to Experience Premium Transportation?</h2>
          <p className="text-xl mb-8">Book your luxury chauffeur service today</p>
          <Link
            to="/contact"
            className="inline-block bg-gold text-black px-8 py-3 rounded-full hover:bg-gold/80 transition-colors font-semibold"
          >
            Contact Us
          </Link>
        </section>
        <div className="text-center mt-8">
          <a href="#top" className="text-gold underline hover:text-gold/80">Back to Top</a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Services; 